import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { PaymentService } from '../payment.service';
import { PaymentMethodType } from '../payment.type';
import { PaymentMethodDialog, PaymentMethodDialogData } from '../payment-method-dialog/payment-method-dialog';
import { AuthService } from '../../auth/auth.service';

/**
 * The Payments "Payout methods" tab: which ways suppliers can be paid. Suppliers
 * save destinations (cards, Shabas, wallet addresses) under active methods and
 * pick one per payment request. Deactivating a method stops new destinations
 * and requests under it; requests already made to it can still be paid. A
 * method in use can only be deactivated, not deleted. Wallet methods also carry
 * the exchange rate their coin amounts are worked out at.
 */
@Component({
  selector: 'app-payment-methods',
  imports: [
    CommonModule, MatButtonModule, MatIconModule, MatProgressSpinnerModule, MatSlideToggleModule,
    MatTableModule, MatTooltipModule,
  ],
  templateUrl: './payment-methods.html',
  styleUrl: './payment-methods.scss',
})
export class PaymentMethods implements OnInit {
  private paymentService = inject(PaymentService);
  readonly canEdit = inject(AuthService).canWrite('suppliers_payments');
  private snackBar = inject(MatSnackBar);
  private dialog = inject(MatDialog);

  readonly columns = ['name', 'kind', 'pattern', 'rate', 'active', 'actions'];

  methods: PaymentMethodType[] = [];
  rateSources: string[] = ['Manual'];
  loading = false;
  error: string | null = null;
  refreshing: string | null = null;

  ngOnInit(): void {
    this.load();
    this.paymentService.getRateSources().subscribe({ next: s => this.rateSources = s });
  }

  load(): void {
    this.loading = true;
    this.error = null;
    this.paymentService.getPaymentMethodTypes().subscribe({
      next: m => { this.methods = m; this.loading = false; },
      error: err => { this.error = err?.error?.error ?? 'Failed to load payout methods'; this.loading = false; },
    });
  }

  openDialog(method?: PaymentMethodType): void {
    const data: PaymentMethodDialogData = { method, rateSources: this.rateSources };
    this.dialog.open(PaymentMethodDialog, { width: '560px', maxWidth: '95vw', data })
      .afterClosed().subscribe((saved: PaymentMethodType | null) => {
        if (!saved) return;
        this.snackBar.open(`${saved.name} saved`, undefined, { duration: 2500 });
        this.load();
      });
  }

  setActive(m: PaymentMethodType, active: boolean): void {
    this.paymentService.updatePaymentMethodType(m.id, { active }).subscribe({
      next: updated => {
        this.replace(updated);
        this.snackBar.open(`${m.name} ${active ? 'activated' : 'deactivated'}`, undefined, { duration: 2500 });
      },
      error: err => { this.fail(err); this.load(); },
    });
  }

  refreshRate(m: PaymentMethodType): void {
    this.refreshing = m.id;
    this.paymentService.refreshPaymentMethodRate(m.id).subscribe({
      next: updated => { this.replace(updated); this.refreshing = null; },
      error: err => { this.refreshing = null; this.fail(err); },
    });
  }

  remove(m: PaymentMethodType): void {
    if (!confirm(`Delete ${m.name}? This only works while no supplier has saved a destination under it.`)) return;
    this.paymentService.deletePaymentMethodType(m.id).subscribe({
      next: () => { this.methods = this.methods.filter(x => x.id !== m.id); },
      error: err => this.fail(err),
    });
  }

  /** The rate a request would use now, as far as this page knows: manual, or the last fetched. */
  currentRate(m: PaymentMethodType): string | undefined {
    return m.rate_source === 'Manual' ? m.manual_rate : m.last_rate;
  }

  /** Older than 30 minutes, a fetched rate is no longer used if the provider is down. */
  isStale(m: PaymentMethodType): boolean {
    return !!m.last_rate_at && Date.now() - new Date(m.last_rate_at).getTime() > 30 * 60_000;
  }

  private replace(updated: PaymentMethodType): void {
    this.methods = this.methods.map(x => x.id === updated.id ? updated : x);
  }

  private fail(err: any): void {
    this.snackBar.open(err?.error?.error ?? 'Failed', 'Close', { duration: 8000 });
  }
}
